import { ColumnState } from 'ag-grid-community';
import { CodeNameModel } from 'components/common/types';
import {
  SearchConfigColumn,
  SearchConfigFilter,
  SearchConfigSortDirection,
} from 'components/common/types/DirectSalesCampaign.types';
import { DirectSalesDealManagementFilters } from '../Filters/Filters.types';
import { columnIds } from './utils';
import { SEARCH_CONFIG_FILTER_MAPPINGS } from './consts';
import { FilterValueType } from './transformSearchConfig.types';

const columnIdsBySearchField = Object.fromEntries(
  Object.entries(columnIds).map(([columnId, searchField]) => [searchField, columnId]),
);

const toColumnKey = (columnId: string): string => columnIds[columnId] ?? columnId;

const toColumnId = (key: string): string => columnIdsBySearchField[key] ?? key;

const toSortDirection = (sort?: string | null): SearchConfigSortDirection | null =>
  sort ? (sort.toUpperCase() as SearchConfigSortDirection) : null;

export const toSearchConfigColumns = (columnState: ColumnState[]): SearchConfigColumn[] =>
  columnState.map(({ colId, hide, sort }) => ({
    key: toColumnKey(colId),
    visible: !hide,
    sort: toSortDirection(sort),
  }));

export const toColumnState = (columns: SearchConfigColumn[]): ColumnState[] =>
  columns.map(({ key, visible, sort }) => ({
    colId: toColumnId(key),
    hide: !visible,
    sort: sort ? (sort.toLowerCase() as ColumnState['sort']) : null,
  }));

const isEmptyFilterValue = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length);

const toSearchConfigFilterValue = (type: FilterValueType, value: unknown): SearchConfigFilter['value'] => {
  if (type === 'date') return new Date(value as Date).toISOString();
  if (type === 'lookup') return (value as CodeNameModel).name;

  return value as SearchConfigFilter['value'];
};

const toFilterValue = (type: FilterValueType, value: SearchConfigFilter['value']): unknown => {
  if (type === 'list') return Array.isArray(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;

  if (type === 'date') {
    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? undefined : date;
  }
  if (type === 'lookup') return { code: value, name: value };

  return value;
};

export const toSearchConfigFilters = (filters: DirectSalesDealManagementFilters): SearchConfigFilter[] =>
  SEARCH_CONFIG_FILTER_MAPPINGS.reduce<SearchConfigFilter[]>((searchConfigFilters, { key, field, operation, type }) => {
    const value = filters[key];

    if (!isEmptyFilterValue(value)) {
      searchConfigFilters.push({ field, operation, value: toSearchConfigFilterValue(type, value) });
    }

    return searchConfigFilters;
  }, []);

export const toFilters = (searchConfigFilters: SearchConfigFilter[]): DirectSalesDealManagementFilters =>
  searchConfigFilters.reduce<DirectSalesDealManagementFilters>((filters, { field, value }) => {
    const mapping = SEARCH_CONFIG_FILTER_MAPPINGS.find((filterMapping) => filterMapping.field === field);
    const filterValue = mapping && toFilterValue(mapping.type, value);

    return mapping && filterValue !== undefined ? { ...filters, [mapping.key]: filterValue } : filters;
  }, {});
